import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  memo,
} from "react";
import axios from "axios";
import { format, isValid, addDays, parseISO } from "date-fns";
import { IndentPDF } from "../../../components/Header/IndentPDF";
import PrinterButton from "../IndentComponents/PrinterButton";
import { useDispatch } from "react-redux";
import { fetchItems } from "../../../store/itemSlice";
import {
  Trash2,
  Edit,
  Plus,
  Printer,
  X,
  Lightbulb,
  Check,
  Loader2,
} from "lucide-react";
import SearchRow from "../IndentComponents/SearchRow";
import Modal from "../IndentComponents/Modal";
import DeleteConfirmationModal from "../IndentComponents/DeleteConfirmationModal";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const NewL2ItemsTable = ({
  indentID,
  onAddItem,
  onDeleteItem,
  calculateTotalAmount,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1a: 0, l1: 0, l2: 0 },
  indentType,
}) => {
  // Add debugging for indentType
  console.log('Debug - NewL2ItemsTable - indentType received:', indentType);
  
  // Add new state to track server-side items
  const [serverItems, setServerItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false); // New state to track initial data load
  const [accountingUnits, setAccountingUnits] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    itemIndex: null,
    itemName: "",
  });
  const [pdfError, setPdfError] = useState("");
  const dispatch = useDispatch();
  // Function to fetch indent data for PDF
  const fetchIndentData = async () => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indents/${indentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    // Fetch items with category information
    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indentitems/${indentID}`
    );
    const itemsData = await itemsResponse.json();

    // Format dates using date-fns
    const formatDate = (dateStr) => {
      if (!dateStr) return "N/A";
      try {
        const date = new Date(dateStr);
        return format(date, "dd/MM/yy");
      } catch (error) {
        console.error('Error formatting date:', error);
        return "N/A";
      }
    };

    return {
      indentId: indentID,
      creationDate: formatDate(indentDetails?.CreationDate),
      PEApprovalDate: formatDate(indentDetails?.PEApprovalDate),
      L1ApprovedDate: formatDate(indentDetails?.L1ApprovedDate),
      L2ApprovalDate: formatDate(indentDetails?.L2ApprovalDate),
      projectName: indentDetails?.ProjectName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      status: indentDetails?.Status || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      invoicingType: indentDetails?.TypeName || "N/A",
      L1: indentDetails?.L1 || 0,
      L2: indentDetails?.L2 || 0,
      PO: indentDetails?.PO || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
      })),
    };
  };

  // Function to get PDF component
  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  useEffect(() => {
    if (indentID) {
      dispatch(fetchItems(indentID))
        .unwrap()
        .then((items) => {
          items.forEach((item) => {
            if (!item.description) {
              fetchItemDescription(item.itemID);
            }
          });
        })
        .catch((error) => {
          console.error("Error fetching items:", error);
        });
    }
  }, [dispatch, indentID]);

  // Add real-time synchronization with polling
  useEffect(() => {
    if (!indentID) return;

    // Initial fetch
    fetchItems2();

    // Set up polling for real-time updates (every 30 seconds)
    const pollInterval = setInterval(() => {
      fetchItems2();
    }, 30000);

    // Cleanup interval on unmount or indentID change
    return () => {
      clearInterval(pollInterval);
    };
  }, [indentID]); // Only depend on indentID

  // Simplified data fetching without infinite loops
  useEffect(() => {
    if (!indentID) return;

    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all data in parallel
        const [categoryResponse, itemResponse, accountingUnitsResponse] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
            axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
            axios.get(`${API_BASE_URL}/dropdown/maudrop`),
          ]);

        setCategories(categoryResponse.data || []);
        setAllItems(itemResponse.data || []);
        setAccountingUnits(accountingUnitsResponse.data || []);
        
        // Fetch items after categories are loaded
        await fetchItems2(categoryResponse.data || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setError("Failed to load initial data. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [indentID]); // Only depend on indentID

  // Modified handleDeleteItem to handle the confirmation flow
  const handleDeleteClick = (index, itemName) => {
    setDeleteConfirmation({
      isOpen: true,
      itemIndex: index,
      itemName: itemName,
    });
  };

  const handleDeleteConfirm = async () => {
    const index = deleteConfirmation.itemIndex;
    const itemToDelete = items[index];

    try {
      await axios.delete(
        `${API_BASE_URL}/viewTable/indentitems/${itemToDelete.indentItemID}`,
        {
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      if (onDeleteItem) {
        onDeleteItem(index);
      }

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    } finally {
      setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
  };

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      // Only fetch descriptions for items missing in cache to avoid repeat calls
      const missing = items.filter((it) => it.itemID && !itemDescriptions[it.itemID]);
      if (missing.length === 0) return;
      missing.forEach((item) => {
        fetchItemDescription(item.itemID);
      });
    }
  }, [items.length]); // Only depend on items.length, not the entire items array

  const getCategoryDays = (categoryID) => {
    const category = categories.find(
      (cat) => cat.CategoryID === parseInt(categoryID)
    );
    return {
      normalDays: category?.NormalDays || 0,
      emergencyDays: category?.EmergencyDays || 0,
    };
  };

  const calculateCommittedDate = (categoryId, type = indentType) => {
    const selectedCategory = categories.find(
      (cat) => cat.CategoryID === parseInt(categoryId)
    );
    
    if (!selectedCategory) {
      return format(new Date(), "yyyy-MM-dd");
    }

    const currentDate = new Date();
    
    // Check if the type contains "emergency" (case-insensitive) to determine which days to use
    const isEmergency = type && type.toLowerCase().includes('emergency');
    const daysToAdd = isEmergency
      ? selectedCategory.EmergencyDays || 0
      : selectedCategory.NormalDays || 0;

    const committedDate = addDays(currentDate, daysToAdd);
    return format(committedDate, "yyyy-MM-dd");
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";

    try {
      const date =
        typeof dateString === "string"
          ? parseISO(dateString)
          : new Date(dateString);

      if (!isValid(date)) {
        console.error("Invalid date:", dateString);
        return "";
      }

      return format(date, "dd/MM/yy");
    } catch (error) {
      console.error("Date formatting error:", error);
      return "";
    }
  };

  const [newItem, setNewItem] = useState({
    categoryID: "",
    itemID: "",
    quantity: 1,
    stock: 0,
    total: 0,
    remarks: "",
    normalDays: 0,
    emergencyDays: 0,
    commitedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
  });

  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  const fetchItems2 = async (categoriesData = null) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { 
            indentId: indentID,
            _t: new Date().getTime() // Add timestamp to prevent caching
          },
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      // Use passed categories or fall back to state
      const categoriesToUse = categoriesData || categories;

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          // If CommitedDate exists in the data, use it; otherwise calculate it
          let committedDate = item.CommitedDate;

          if (!committedDate) {
            // Get category details using the passed categories
            const selectedCategory = categoriesToUse.find(
              (cat) => cat.CategoryID === parseInt(item.CategoryID)
            );
            
            if (selectedCategory) {
              // Check if the type contains "emergency" (case-insensitive) to determine which days to use
              const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
              const daysToAdd = isEmergency
                ? selectedCategory.EmergencyDays || 0
                : selectedCategory.NormalDays || 0;

              // Calculate committed date using the selected category
              const currentDate = new Date();
              const calculatedDate = addDays(currentDate, daysToAdd);
              committedDate = format(calculatedDate, "yyyy-MM-dd");
            } else {
              // Fallback to current date if category not found
              committedDate = format(new Date(), "yyyy-MM-dd");
            }
          }

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            normalDays: item.NormalDays,
            emergencyDays: item.EmergencyDays,
            commitedDate: committedDate,
            accountingUnitID: item.AccountingUnitID,
            unitname: item.AU,
          };
        });

      setServerItems(fetchedItems);
      setItems(fetchedItems);
    } catch (error) {
      console.error("Error fetching items:", error);
      setError("Failed to load items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Removed redundant effect that was causing duplicate fetches

  const handleItemChange = (itemId) => {
    const parsedItemId = parseInt(itemId, 10);
    const selectedItem = filteredItems.find(
      (item) => item.ItemID === parsedItemId
    );

    setNewItem((prev) => ({
      ...prev,
      itemID: parsedItemId,
      accountingUnitID: selectedItem?.AccountingUnitID || "",
              accountingUnitName: selectedItem?.AU || "",
      stock: selectedItem?.Stock || 0,
    }));
  };

  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setNewItem((prev) => ({
        ...prev,
        categoryID: "",
        itemID: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      }));
      setFilteredItems([]);
      return;
    }
    const parsedCategoryId = parseInt(categoryId, 10);
    const selectedCategory = categories.find(
      (cat) => cat.CategoryID === parsedCategoryId
    );

    const committedDate = calculateCommittedDate(parsedCategoryId);
    setNewItem((prev) => ({
      ...prev,
      categoryID: parsedCategoryId,
      itemID: "", // Reset itemID
      normalDays: selectedCategory?.NormalDays || 0,
      emergencyDays: selectedCategory?.EmergencyDays || 0,
      commitedDate: committedDate,
      accountingUnitID: "", // Reset accountingUnitID
      accountingUnitName: "", // Reset accountingUnitName
    }));

    const filtered = allItems.filter(
      (item) => item.CategoryID === parsedCategoryId
    );
    setFilteredItems(filtered);
  };

  const handleQuantityChange = (value) => {
    // Allow decimal values up to 3 places
    const regex = /^\d*\.?\d{0,3}$/;
    if (regex.test(value)) {
      setNewItem((prev) => ({
        ...prev,
        quantity: value,
      }));
    }
  };

  // Modify handleAddNewItem to include committed date
  const handleAddNewItem = async () => {
    if (loading) return;
    setLoading(true);

    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    const formattedQuantity = Math.round(newItem.quantity);

    try {
      const isDuplicateItem = items.some(
        (existingItem) => existingItem.itemID === newItem.itemID
      );

      if (isDuplicateItem) {
        setError(
          `Item "${
            filteredItems.find((item) => item.ItemID === newItem.itemID)
              ?.ItemName
          }" is already added.`
        );
        return;
      }

      const selectedItem = filteredItems.find(
        (item) => item.ItemID === newItem.itemID
      );

      const accountingUnitID = selectedItem?.AccountingUnitID;
      if (!accountingUnitID) {
        setError(
          "Could not determine the Accounting Unit for the selected item."
        );
        return;
      }

      // Include committed date in the POST request
      await axios.post(`${API_BASE_URL}/l2page/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: formattedQuantity,
        Description: newItem.remarks || "",
        AccountingUnitID: accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
        CommitedDate: newItem.commitedDate,
      });

      const updatedItem = {
        ...newItem,
        itemName: selectedItem ? selectedItem.ItemName : "Unnamed Item",
        total: newItem.quantity * newItem.stock,
        accountingUnitID: accountingUnitID,
      };

      setItems((prevItems) => [...prevItems, updatedItem]);
      setModalOpen(false);
      setError(null);

      // Reset form
      setNewItem({
        categoryID: "",
        itemID: "",
        quantity: 1,
        stock: 0,
        total: 0,
        remarks: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        commitedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      });

      fetchItems2();
      onAddItem && onAddItem(updatedItem);
      onUpdateItem &&
        onUpdateItem({
          type: "add",
          item: updatedItem,
          changeLog: {
            ...changeLog,
            addedItems: [...changeLog.addedItems, updatedItem],
          },
        });
    } catch (error) {
      console.error("Error adding item:", error);
      setError("Failed to add item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewItemFieldChange = useCallback((field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = Math.round(parseFloat(value));
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleDeleteItem = async (index) => {
    const itemToDelete = items[index];
    try {
      await axios.delete(
        `${API_BASE_URL}/l2page/indentitems/${itemToDelete.indentItemID}`,
        {
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      onDeleteItem && onDeleteItem(index);

      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    }
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => total + item.total, 0);
  };

  const verifyItems = async () => {
    try {
      if (!indentID) {
        setError("Invalid indent ID");
        return null;
      }

      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const indentItems = response.data.filter(
        (item) => parseInt(item.IndentID) === parseInt(indentID)
      );

      if (!Array.isArray(indentItems) || indentItems.length === 0) {
        setError("Please add items before saving");
        return null;
      }

      return indentItems.map((item) => {
        // Always use the manually edited committed date if present in the current items state
        const currentItem = items.find(i => i.indentItemID === item.IndentItemID);
        let committedDate = currentItem && currentItem.commitedDate
          ? currentItem.commitedDate
          : calculateCommittedDate(item.CategoryID, indentType);
        return {
          indentItemID: item.IndentItemID,
          itemID: item.ItemID,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          remarks: item.Description || "",
          categoryID: item.CategoryID,
          normalDays: item.NormalDays,
          emergencyDays: item.EmergencyDays,
          promisedDate: item.PromisedDate,
          commitedDate: committedDate,
          accountingUnitID: item.AccountingUnitID,
        };
      });
    } catch (error) {
      console.error("Error verifying items:", error);
      setError(`Failed to verify items: ${error.message}`);
      return null;
    }
  };

  // Update the handleFinalSubmit function to include committed date
  const handleFinalSubmit = async () => {
    try {
      const verifiedItems = await verifyItems();
      if (!verifiedItems) return;

      // The verifyItems function now handles committed date logic properly
      // No need to recalculate here as it's already done in verifyItems
      const submissionData = {
        indentId: parseInt(indentID),
        items: verifiedItems,
        L2ApprovalDate: format(new Date(), "yyyy-MM-dd") // Add L2ApprovalDate in yyyy-MM-dd format
      };

      await onSubmit(submissionData);

      // After successful submission, refresh the indent data to get updated dates
      const updatedIndentData = await fetchIndentData();
      setItems(updatedIndentData.items);

      setChangeLog({
        addedItems: [],
        deletedItems: [],
        updatedItems: [],
      });

      setError(null);
    } catch (error) {
      console.error("Error during submission:", error);
      setError(`Failed to submit: ${error.message}`);
    }
  };

  const handleUpdateDescription = async (itemId, newDescription) => {
    try {
      await axios.put(`${API_BASE_URL}/viewTable/itemdescription/${itemId}`, {
        Description: newDescription,
        IndentID: indentID,
      });

      setItemDescriptions((prev) => ({
        ...prev,
        [itemId]: newDescription,
      }));

      setEditingItem((prev) => ({
        ...prev,
        description: newDescription,
        isEditingDescription: false,
      }));
    } catch (error) {
      console.error("Error updating description:", error);
      setError("Failed to update description. Please try again.");
    }
  };

  // Update handleUpdateItem to include committed date
  const handleUpdateItem = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const itemToUpdate = editingItem;
      if (!itemToUpdate.itemID || !itemToUpdate.indentItemID) {
        setError("Invalid item ID");
        return;
      }

      const formattedQuantity = Math.round(editingItem.quantity);
      // Use the manually edited committed date if it exists, otherwise use the current value
      const committedDate = itemToUpdate.commitedDate;

      await axios.put(
        `${API_BASE_URL}/l2page/indentitems/${itemToUpdate.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: parseFloat(itemToUpdate.stock) || 0,
          CommitedDate: committedDate,
          Description: editingItem.description, // Add description to the update
        }
      );

      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));

      // Update local state with all fields
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.indentItemID === itemToUpdate.indentItemID
            ? {
                ...item,
                quantity: formattedQuantity,
                stock: parseFloat(itemToUpdate.stock) || 0,
                commitedDate: committedDate,
                remarks: editingItem.description, // Update remarks in items array
              }
            : item
        )
      );

      setEditingItem(null);
      const updatedItems = items.map((item) => {
        if (item.indentItemID === editingItem.indentItemID) {
          return {
            ...item,
            quantity: formattedQuantity,
            stock: editingItem.stock,
            remarks: editingItem.description, // Update remarks in items array
            commitedDate: committedDate,
          };
        }
        return item;
      });

      onUpdateItem({
        type: "update",
        item: editingItem,
        items: updatedItems,
      });

      onUpdateItem &&
        onUpdateItem({
          type: "update",
          item: itemToUpdate,
          changeLog: {
            ...changeLog,
            updatedItems: [
              ...changeLog.updatedItems,
              {
                original: items[itemToUpdate.index],
                updated: itemToUpdate,
              },
            ],
          },
        });
    } catch (error) {
      console.error("Error updating item:", error);
      setError("Failed to update item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleItemSearch = useCallback(
    (selectedItem) => {
      // Handle null/clearing case
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "", 
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          emergencyDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      // First set filtered items for the selected category
      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      // Get the normalDays from the category
      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );
      const normalDays = selectedCategory?.NormalDays || 0;
      const emergencyDays = selectedCategory?.EmergencyDays || 0;

      // Calculate dates based on indent type
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      const daysToAdd = indentType === 'normal' ? normalDays : emergencyDays;
      promisedDate.setDate(currentDate.getDate() + daysToAdd);

      // Update all state in one go
      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.AU || "",
        stock: selectedItem.Stock || 0,
        normalDays: normalDays,
        emergencyDays: emergencyDays,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
      }));
    },
    [allItems, categories, indentType]
  );

  // Update handleSaveDraft to include committed date
  const handleSaveDraft = async () => {
    try {
      const verifiedItems = await verifyItems();
      if (!verifiedItems) return;

      // The verifyItems function now handles committed date logic properly
      // No need to recalculate here as it's already done in verifyItems
      await axios.put(
        `${API_BASE_URL}/l2page/indents/${indentID}/draft`,
        {
          isDraft: true,
          items: verifiedItems,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      onSaveDraft && onSaveDraft();
    } catch (error) {
      console.error("Error saving draft:", error);
      setError("Failed to save draft. Please try again.");
    }
  };

  const isSubmittedStage =
    indentStatus.l1a === 1 && indentStatus.l1 === 1 && indentStatus.l2 === 1;

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A to open add modal
      if (
        e.ctrlKey &&
        e.key.toLowerCase() === "a" &&
        !modalOpen &&
        isDraft &&
        isEditable &&
        !isSubmittedStage
      ) {
        e.preventDefault(); // Prevent default browser "Select All" behavior
        setModalOpen(true);
      }

      // Ctrl+S to save item
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault(); // Prevent the default browser action (save)

        // Trigger the "Add Item" function or button click
        document.getElementById("addItemButton").click(); // Assuming your button has this ID
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, editingItem, isDraft, isEditable, isSubmittedStage]);

  const renderActions = (item, index) => (
    <td className="px-4 py-3 text-center">
      <div className="flex justify-center space-x-2">
        {isDraft && isEditable && (
          <>
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => handleEditItem(item, index)}
            >
              <Edit size={18} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => handleDeleteClick(index, item.itemName)}
            >
              <Trash2 size={18} />
            </button>
          </>
        )}
      </div>
    </td>
  );

  const renderDaysInput = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <div className="col-span-1">
        <label className="block text-sm font-medium mb-1">
          {isEmergency ? "Days" : "Days"}
        </label>
        <input
          type="text"
          className="border rounded w-full px-3 py-2 bg-gray-100"
          value={
            isEmergency
              ? newItem.emergencyDays || "0"
              : newItem.normalDays || "0"
          }
          readOnly
        />
      </div>
    );
  };

  const handleDescriptionChange = useCallback((e) => {
    setEditingItem((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  }, []);

  const renderDescriptionCell = useCallback((itemId) => {
    if (editingItem?.itemID === itemId) {
      return (
        <div className="flex items-center space-x-2">
          <input
            key={`description-${itemId}-${editingItem.description}`}
            type="text"
            className="border rounded px-2 py-1 text-sm w-32"
            value={editingItem.description}
            onChange={handleDescriptionChange}
            autoFocus
          />
        </div>
      );
    }

    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  }, [editingItem, itemDescriptions, loadingDescriptions, handleDescriptionChange, fetchItemDescription]);

  const handleEditItem = (item, index) => {
    // Ensure we fetch the description if it hasn't been loaded yet
    if (!itemDescriptions[item.itemID]) {
      fetchItemDescription(item.itemID);
    }

    setEditingItem({
      ...item,
      index: index,
      indentItemID: item.indentItemID,
      quantity: item.quantity,
      stock: item.stock,
      description: itemDescriptions[item.itemID] || "",
      commitedDate: item.commitedDate || new Date().toISOString().split("T")[0],
    });
    // Don't toggle itemsUpdated here; it triggers refresh effects unnecessarily
  };

  const L2DesktopRow = memo(function L2DesktopRow({
    item,
    index,
    isSubmittedStage,
    editingItem,
    renderDescriptionCell,
    onQuantityChange,
    onSave,
    onCancel,
    onEdit,
    onDelete,
  }) {
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors duration-200">
        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
        <td className="px-4 py-3 font-medium">{item.itemName}</td>
        <td className="px-4 py-3 text-center">{renderDescriptionCell(item.itemID)}</td>
        <td className="px-4 py-3 font-medium">{item.unitname}</td>
        {editingItem && editingItem.index === index ? (
          <>
            <td className="px-4 py-3">
              <input
                type="number"
                step="1"
                min="0"
                className="border rounded w-full px-2 py-1"
                value={editingItem.quantity}
                onChange={(e) => onQuantityChange(e.target.value)}
              />
            </td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            <td className="px-4 py-3 text-right">{formatDisplayDate(item.commitedDate)}</td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-green-500 hover:text-green-700" onClick={onSave}>Save</button>
                  <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>Cancel</button>
                </div>
              </td>
            )}
          </>
        ) : (
          <>
            <td className="px-4 py-3 text-center">{Math.round(item.quantity)}</td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            <td className="px-4 py-3 text-right">{formatDisplayDate(item.commitedDate)}</td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-blue-500 hover:text-blue-700" onClick={() => onEdit(item, index)}>
                    <Edit size={18} />
                  </button>
                  <button className="text-red-500 hover:text-red-700" onClick={() => onDelete(index, item.itemName)}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            )}
          </>
        )}
      </tr>
    );
  });

  return (
    <div className="bg-white shadow-md rounded-lg px-4 border border-gray-200">
      {/* Reorganized header section */}
      <div className="flex justify-between items-center border-b">
        <div className="flex-1">
          {isDraft && isEditable && !isSubmittedStage && (
            <div className="text-sm text-gray-500">
              Keyboard shortcuts: Ctrl+A to add item, Ctrl+S to save
              {modalOpen ? ", Ctrl+S to save" : ""}
            </div>
          )}
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 flex-1 text-center">
          Indent Items
        </h2>

        <div className="flex items-center space-x-2 flex-1 justify-end">
          {/* Print Button */}
          <PrinterButton
            getPDFComponent={getPDFComponent}
            filename={`indent-${indentID}`}
            fetchData={fetchIndentData}
            onError={(error) => setPdfError("Failed to generate PDF. Please try again.")}
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
            title="Print Invoice"
          />

          {!isSubmittedStage && isDraft && isEditable && (
            <button
              className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
              onClick={() => setModalOpen(true)}
              title="Add Item"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Committed Date</th>
              {!isSubmittedStage && (
                <th className="px-4 py-3 text-center w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <L2DesktopRow
                  key={index}
                  item={item}
                  index={index}
                  isSubmittedStage={isSubmittedStage}
                  editingItem={editingItem}
                  renderDescriptionCell={renderDescriptionCell}
                  onQuantityChange={(val) => setEditingItem((prev) => ({ ...prev, quantity: val }))}
                  onSave={handleUpdateItem}
                  onCancel={() => setEditingItem(null)}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteClick}
                />
              ))
            ) : (
              <tr>
                <td colSpan={!isSubmittedStage ? 8 : 7} className="text-center text-gray-500 py-6">
                  Loading items...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-between items-center">
        <div className="flex space-x-4">
          {isSubmittedStage ? (
            <button
              className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
              disabled
            >
              Submitted
            </button>
          ) : (
            <>
              {isDraft && isEditable && (
                <>
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={onSaveDraft}
                    disabled={!isDraft}
                  >
                    Save Draft
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={handleFinalSubmit}
                    disabled={!isDraft}
                  >
                    Submit Indent
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <Modal
          onClose={() => {
            setModalOpen(false);
            setError(null);
          }}
        >
          <div className="flex flex-col">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <h2 className="text-xl font-bold text-center mb-4">Add New Item</h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddNewItem();
              }}
            >
              <SearchRow
                allItems={allItems.map((item) => ({
                  ...item,
                  CategoryName: categories.find(
                    (cat) => cat.CategoryID === item.CategoryID
                  )?.CategoryName,
                }))}
                onSelectItem={handleItemSearch}
                selectedItemId={newItem.itemID}
              />

              <div className="grid grid-cols-12 gap-4 mb-4">
                <div className="col-span-3">
                  <label className="block text-sm font-medium mb-1">
                    Accounting Unit
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2 bg-gray-50"
                    value={newItem.accountingUnitName || "Not set"}
                    readOnly
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Up to 3 decimals"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Stock
                  </label>
                  <input
                    type="number"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.stock}
                    onChange={(e) =>
                      handleNewItemFieldChange(
                        "stock",
                        parseFloat(e.target.value)
                      )
                    }
                    required
                  />
                </div>
                {renderDaysInput()}
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">
                    Promised Date
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2 bg-gray-100"
                    value={
                      newItem.promisedDate
                        ? new Date(newItem.promisedDate).toLocaleDateString()
                        : "Not set"
                    }
                    readOnly
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium">Remarks</label>
                <textarea
                  key={newItem.itemID} // Add a stable key to prevent re-renders
                  className="border rounded w-full px-3 py-2"
                  value={newItem.remarks}
                  onChange={(e) =>
                    handleNewItemFieldChange("remarks", e.target.value)
                  }
                  placeholder="Optional remarks about the item"
                  rows="3"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  id="addItemButton"
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded"
                >
                  Add Item
                </button>
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirmation.itemName}
      />
    </div>
  );
};

export default NewL2ItemsTable;
